import {
  ComplianceEvent,
  AssuranceTaskRaw,
  AssetRaw,
  RiskRaw,
  SupportingDocRaw,
  ComplianceData,
  Assignee,
  FirebaseTimestamp
} from '@/types/compliance';
import type { Checklist, ChecklistRepeatConfig } from '@/app/pages/checklists/types/checklist.types';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  Timestamp
} from 'firebase/firestore';
// Import db and storage from main_config instead of firebase.ts
import { db } from '@/js/external-js/config/main_config';
import { getAuthUser } from '@/lib/authState';

import { getPolicyList } from '@/services/policyService';
import { Policy } from '@/types/policy';
import { getProcesses, Process, ProcessRepeatConfig } from '@/services/processService';

// Asset and Risk Register namespaces might be in the external config
// We'll need to implement our own versions if they're not available
import { AssetRegister } from '@/js/external-js/variableModules/namespaces/AssetRegister.namespace.js';
import { RiskRegister } from '@/js/external-js/variableModules/namespaces/RiskRegister.namespace.js';


/**
 * Format a date to a readable string
 * @param date Date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date | string | FirebaseTimestamp | null): string {
  if (!date) return '';

  // Handle Firebase Timestamp objects
  if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    date = date.toDate();
  }

  // Convert string to Date object if needed
  if (typeof date === 'string') {
    date = new Date(date);
  }

  const userLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-AU'; // Use browser's locale
  // Now date is definitely a Date object, so we can use toLocaleDateString
  return (date as Date).toLocaleDateString(userLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
}

function normalizeDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function parseChecklistDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return normalizeDate(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : normalizeDate(parsed);
  }
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as any).toDate === 'function'
  ) {
    const parsed = (value as { toDate: () => Date }).toDate();
    return normalizeDate(parsed);
  }
  if (
    value &&
    typeof value === 'object' &&
    'seconds' in value &&
    typeof (value as any).seconds === 'number'
  ) {
    return normalizeDate(new Date((value as any).seconds * 1000));
  }
  return null;
}

function formatDateToYMD(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseFilterBoundary(value?: Date | string | null): Date | null {
  if (!value) return null;
  const parsed = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return normalizeDate(parsed);
}

function addInterval(base: Date, frequency: ChecklistRepeatConfig['frequency'], interval: number): Date | null {
  if (frequency === 'never') {
    return null;
  }

  const next = new Date(base);
  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + interval);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7 * interval);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + interval);
      break;
    case 'quarterly':
      next.setMonth(next.getMonth() + 3 * interval);
      break;
    case 'annually':
      next.setFullYear(next.getFullYear() + interval);
      break;
    default:
      return null;
  }

  return normalizeDate(next);
}

function generateRecurringOccurrences(
  startDate: Date,
  repeat: ChecklistRepeatConfig,
  filter?: ComplianceDataFilter
): { occurrence: Date; previous: Date | null }[] {
  if (!repeat || repeat.frequency === 'never') {
    return [];
  }

  const occurrences: { occurrence: Date; previous: Date | null }[] = [];
  const interval = Math.max(1, repeat.interval ?? 1);
  let candidate = normalizeDate(startDate);
  let previous: Date | null = null;

  const rangeStart = parseFilterBoundary(filter?.startDate ?? null);
  const rangeEndRaw = parseFilterBoundary(filter?.endDate ?? null);
  const rangeEnd = rangeEndRaw
    ? (() => {
        const adjusted = new Date(rangeEndRaw);
        adjusted.setDate(adjusted.getDate() + 1);
        adjusted.setHours(0, 0, 0, 0);
        return adjusted;
      })()
    : null;
  const repeatEnd = repeat.endDate ? parseChecklistDate(repeat.endDate) : null;

  if (repeatEnd && candidate > repeatEnd) {
    return [];
  }

  const maxIterations = 1000;
  let safetyCounter = 0;
  const noUpperBound = !rangeEnd && !repeatEnd;
  const maxOccurrencesWithoutBound = 365;
  let occurrenceCount = 0;

  const lowerBound = rangeStart ?? candidate;

  while (candidate < lowerBound) {
    const next = addInterval(candidate, repeat.frequency, interval);
    if (!next) {
      return [];
    }
    previous = candidate;
    candidate = next;
    if (repeatEnd && candidate > repeatEnd) {
      return [];
    }
    if (++safetyCounter > maxIterations) {
      console.warn('[ComplianceService] Recurrence calculation aborted while fast-forwarding.');
      return [];
    }
  }

  safetyCounter = 0;
  while (true) {
    if (repeatEnd && candidate > repeatEnd) {
      break;
    }
    if (rangeEnd && candidate >= rangeEnd) {
      break;
    }
    occurrences.push({
      occurrence: new Date(candidate),
      previous: previous ? new Date(previous) : null,
    });
    occurrenceCount += 1;

    const next = addInterval(candidate, repeat.frequency, interval);
    if (!next) {
      break;
    }
    previous = candidate;
    candidate = next;

    if (++safetyCounter > maxIterations) {
      console.warn('[ComplianceService] Recurrence calculation aborted due to excessive iterations.');
      break;
    }

    if (noUpperBound && occurrenceCount >= maxOccurrencesWithoutBound) {
      break;
    }
  }

  return occurrences;
}

async function getChecklistEvents(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching checklists for company ID: ${companyID}`);

    const checklistsSnapshot = await getDocs(collection(db, 'customers', companyID, 'checklists'));
    if (checklistsSnapshot.empty) {
      return [];
    }

    const events: ComplianceEvent[] = [];

    checklistsSnapshot.forEach(docSnap => {
      const data = docSnap.data() as Checklist;

      const reviewDate = parseChecklistDate(data.reviewDate ?? data.nextReviewDate ?? data.lastReviewDate ?? null);
      const repeat = data.repeat;

      let occurrenceDate: Date | null = null;
      let previousDate: Date | null = null;

      const start = repeat ? parseChecklistDate(repeat.startDate ?? reviewDate ?? null) : null;
      const end = repeat?.endDate ? parseChecklistDate(repeat.endDate) : null;

      if (repeat && repeat.frequency && repeat.frequency !== 'never') {
        if (!start) {
          return;
        }

        const occurrences = generateRecurringOccurrences(start, repeat, filter);
        if (!occurrences.length) {
          return;
        }

        const assigneeId = data.createdBy || null;
        const assignee = assignees.find(a => a.userID === assigneeId);
        const assigneeName = assignee ? `${assignee.firstName} ${assignee.lastName}` : undefined;

        occurrences.forEach(({ occurrence, previous }) => {
          if (!isWithinDateRange(occurrence, filter)) {
            return;
          }

          if (filter?.userId && assigneeId && assigneeId !== filter.userId) {
            return;
          }

          events.push({
            id: `${docSnap.id}-${formatDateToYMD(occurrence)}`,
            checklistId: docSnap.id,
            date: formatDateToYMD(occurrence),
            title: data.title || 'Checklist review',
            description: data.description,
            type: 'checklist',
            assignee: assigneeName,
            prevReviewDate: previous ? formatDateToYMD(previous) : undefined,
            reviewDate: reviewDate ? formatDateToYMD(reviewDate) : undefined,
            nextReviewDate: formatDateToYMD(occurrence),
            startDate: start ? formatDateToYMD(start) : undefined,
            endDate: repeat ? (end ? formatDateToYMD(end) : null) : undefined,
            interval: repeat?.interval,
            frequency: repeat?.frequency,
            rawData: data,
          });
        });
      } else if (reviewDate) {
        if (!isWithinDateRange(reviewDate, filter)) {
          return;
        }

        const assigneeId = data.createdBy || null;
        const assignee = assignees.find(a => a.userID === assigneeId);
        const assigneeName = assignee ? `${assignee.firstName} ${assignee.lastName}` : undefined;

        if (filter?.userId && assigneeId && assigneeId !== filter.userId) {
          return;
        }

        events.push({
          id: docSnap.id,
          checklistId: docSnap.id,
          date: formatDateToYMD(reviewDate),
          title: data.title || 'Checklist review',
          description: data.description,
          type: 'checklist',
          assignee: assigneeName,
          reviewDate: formatDateToYMD(reviewDate),
          startDate: start ? formatDateToYMD(start) : undefined,
          endDate: repeat ? (end ? formatDateToYMD(end) : null) : undefined,
          interval: repeat?.interval,
          frequency: repeat?.frequency,
          rawData: data,
        });
      }
    });

    console.log(`✅ [ComplianceService] Loaded ${events.length} checklists`);
    return events;
  } catch (error) {
    console.error('[complianceService] Error getting checklists:', error);
    return [];
  }
}


/**
 * Filter options for compliance data queries
 */
export interface ComplianceDataFilter {
  startDate?: Date | string; // Filter events from this date
  endDate?: Date | string;   // Filter events to this date
  userId?: string;           // Filter by assignee user ID
  view?: 'day' | 'week' | 'month' | 'year'; // Calendar view being used
}

/**
 * Get process events for the compliance calendar
 * Supports both one-time and recurring processes
 */
async function getProcessEvents(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching processes for company ID: ${companyID}`);
    
    const processes = await getProcesses(companyID);
    if (!processes.length) {
      return [];
    }
    
    const events: ComplianceEvent[] = [];
    
    for (const process of processes) {
      const reviewDate = parseChecklistDate(process.reviewDate ?? process.nextReviewDate ?? null);
      const repeat = process.repeat;
      
      // Find the process owner
      const ownerId = process.owner || process.createdBy;
      const assignee = assignees.find(a => a.userID === ownerId);
      const assigneeName = assignee ? `${assignee.firstName} ${assignee.lastName}` : undefined;
      
      // Apply user filter if specified
      if (filter?.userId && ownerId && ownerId !== filter.userId) {
        continue;
      }
      
      const start = repeat ? parseChecklistDate(repeat.startDate ?? reviewDate ?? null) : null;
      const end = repeat?.endDate ? parseChecklistDate(repeat.endDate) : null;
      
      // Handle recurring processes
      if (repeat && repeat.frequency && repeat.frequency !== 'never') {
        if (!start) {
          continue;
        }
        
        // Convert ProcessRepeatConfig to ChecklistRepeatConfig format for reuse
        const checklistRepeat: ChecklistRepeatConfig = {
          frequency: repeat.frequency as any,
          interval: repeat.interval,
          startDate: start,
          endDate: end ?? undefined,
        };
        
        const occurrences = generateRecurringOccurrences(start, checklistRepeat, filter);
        if (!occurrences.length) {
          continue;
        }
        
        occurrences.forEach(({ occurrence, previous }) => {
          if (!isWithinDateRange(occurrence, filter)) {
            return;
          }
          
          events.push({
            id: `process-${process.id}-${formatDateToYMD(occurrence)}`,
            processId: process.id,
            date: formatDateToYMD(occurrence),
            title: process.name || 'Process review',
            description: process.description,
            type: 'process',
            assignee: assigneeName,
            prevReviewDate: previous ? formatDateToYMD(previous) : undefined,
            reviewDate: reviewDate ? formatDateToYMD(reviewDate) : undefined,
            nextReviewDate: formatDateToYMD(occurrence),
            startDate: start ? formatDateToYMD(start) : undefined,
            endDate: repeat ? (end ? formatDateToYMD(end) : null) : undefined,
            interval: repeat?.interval,
            frequency: repeat?.frequency,
            rawData: process,
          });
        });
      } else if (reviewDate) {
        // Non-recurring process with a review date
        if (!isWithinDateRange(reviewDate, filter)) {
          continue;
        }
        
        events.push({
          id: `process-${process.id}`,
          processId: process.id,
          date: formatDateToYMD(reviewDate),
          title: process.name || 'Process review',
          description: process.description,
          type: 'process',
          assignee: assigneeName,
          reviewDate: formatDateToYMD(reviewDate),
          startDate: start ? formatDateToYMD(start) : undefined,
          endDate: repeat ? (end ? formatDateToYMD(end) : null) : undefined,
          interval: repeat?.interval,
          frequency: repeat?.frequency,
          rawData: process,
        });
      }
    }
    
    console.log(`✅ [ComplianceService] Loaded ${events.length} process events`);
    return events;
  } catch (error) {
    console.error('[complianceService] Error getting processes:', error);
    return [];
  }
}

/**
 * Helper function to filter events by date range
 * @param date Event date as string or Date
 * @param filter Filter parameters
 * @returns True if event should be included, false otherwise
 */
function isWithinDateRange(date: string | Date, filter?: ComplianceDataFilter): boolean {
  if (!filter || (!filter.startDate && !filter.endDate)) {
    return true; // No date filter specified, include all
  }

  // Convert string date to Date object if needed
  let eventDate: Date;
  if (typeof date === 'string') {
    eventDate = new Date(date);
  } else if (date instanceof Date) {
    eventDate = date;
  } else {
    return false; // Invalid date format
  }

  // Reset time to midnight for consistent date comparisons
  eventDate.setHours(0, 0, 0, 0);

  // Check start date (inclusive)
  if (filter.startDate) {
    const startDate = new Date(filter.startDate);
    startDate.setHours(0, 0, 0, 0);
    if (eventDate < startDate) {
      return false;
    }
  }

  // Check end date (inclusive)
  if (filter.endDate) {
    const endDate = new Date(filter.endDate);
    endDate.setHours(23, 59, 59, 999); // End of day
    if (eventDate > endDate) {
      return false;
    }
  }

  return true;
}

/**
 * Fetches all compliance events from various sources and combines them
 * @param companyID The company ID to fetch events for
 * @param filter Optional filtering parameters
 * @returns Promise containing structured compliance data
 */
export async function getComplianceData(companyID: string, filter?: ComplianceDataFilter): Promise<ComplianceData> {
  console.log(`🔄 [ComplianceService] Fetching compliance data for company: ${companyID}`);
  if (filter) {
    console.log('📅 [ComplianceService] Applying filters:', {
      startDate: filter.startDate ? new Date(filter.startDate).toISOString().split('T')[0] : 'none',
      endDate: filter.endDate ? new Date(filter.endDate).toISOString().split('T')[0] : 'none',
      userId: filter.userId || 'none',
      view: filter.view || 'none'
    });
  }

  try {
    // Get all assignees
    const assignees = await getAssignees(companyID);

    // Get all compliance data types in parallel
    const [assuranceTasks, assets, risks, supportingDocs, policies, checklistEvents, correctiveActions, processEvents] = await Promise.all([
      getAssuranceTasks(companyID, assignees, filter),
      getAssets(companyID, assignees, filter),
      getRisks(companyID, assignees, filter),
      getSupportingDocs(companyID, assignees, filter),
      getPolicies(companyID, assignees, filter),
      getChecklistEvents(companyID, assignees, filter),
      getCorrectiveActions(companyID, assignees, filter),
      getProcessEvents(companyID, assignees, filter)
    ]);

    // Combine all events into a single array
    let allEvents = [
      ...assuranceTasks,
      ...assets,
      ...risks,
      ...supportingDocs,
      ...policies,
      ...checklistEvents,
      ...correctiveActions,
      ...processEvents,
    ];

    // Sort by date
    allEvents.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    console.log(`🎉 [ComplianceService] Total compliance events: ${allEvents.length}`);
    console.log('📊 [ComplianceService] Event type breakdown:');
    console.log(`   • Assurance Tasks: ${assuranceTasks.length}`);
    console.log(`   • Assets: ${assets.length}`);
    console.log(`   • Risks: ${risks.length}`);
    console.log(`   • Supporting Docs: ${supportingDocs.length}`);
    console.log(`   • Policies: ${policies.length}`);
    console.log(`   • Checklists: ${checklistEvents.length}`);
    console.log(`   • Corrective Actions: ${correctiveActions.length}`);
    console.log(`   • Processes: ${processEvents.length}`);

    // Log a preview of the first few events (if available)
    if (allEvents.length > 0) {
      console.log('📝 [ComplianceService] Sample event preview:');
      console.log(allEvents[0]);
    }

    return {
      assuranceTasks,
      assets,
      risks,
      supportingDocs,
      checklists: checklistEvents,
      correctiveActions,
      processes: processEvents,
      allEvents,
    };
  } catch (error) {
    console.error('[ComplianceService] Error fetching compliance data:', error);
    // Return empty data
    return {
      assuranceTasks: [],
      assets: [],
      risks: [],
      supportingDocs: [],
      checklists: [],
      correctiveActions: [],
      processes: [],
      allEvents: []
    };
  }
}

/**
 * Get assignees for the company
 * @param companyID Company ID
 * @returns Array of assignee objects
 */
async function getAssignees(companyID: string): Promise<Assignee[]> {
  try {
    console.log(`[complianceService] Getting assignees for company ID: ${companyID}`);

    // Fetch the organization users from the customer document
    const customerDoc = await getDoc(doc(db, 'customers', companyID));
    if (!customerDoc.exists()) {
      throw new Error('Customer document not found');
    }

    const customerData = customerDoc.data();
    const orgUsers = customerData?.customerOrg?.users || {};

    // Get user IDs from the organization
    const userIDs = Object.values(orgUsers)
      .map((u: any) => u.userID)
      .filter(Boolean);

    // Fetch user details for each user ID
    const assigneesPromises = userIDs.map(async (userID: string) => {
      const userDoc = await getDoc(doc(db, 'users', userID));
      if (!userDoc.exists()) return null;

      const userData = userDoc.data();
      return {
        userID,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        companyName: userData.companyName || '',
        mspUser: userData.mspUser || false,
        mspUsers: userData.mspUsers || []
      };
    });

    const assignees = (await Promise.all(assigneesPromises)).filter(Boolean) as Assignee[];
    console.log(`[complianceService] Found ${assignees.length} assignees`);
    return assignees;
  } catch (error) {
    console.error('[complianceService] Error getting assignees:', error);
    return [];
  }
}

/**
 * Get corrective actions for the company
 * @param companyID Company ID
 * @param assignees List of assignees
 * @param filter Optional filtering parameters
 * @returns Promise containing array of corrective action events
 */
async function getCorrectiveActions(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching corrective actions for company ID: ${companyID}`);

    // Fetch corrective actions from Firestore
    const correctiveActionsCollection = collection(db, 'customers', companyID, 'correctiveActions');
    const correctiveActionsSnapshot = await getDocs(correctiveActionsCollection);
    
    if (correctiveActionsSnapshot.empty) {
      return [];
    }

    const correctiveActions: ComplianceEvent[] = [];

    // Process corrective actions
    correctiveActionsSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      
      // Skip if no due date
      if (!data.dueDate) {
        return;
      }

      // Convert due date to Date object
      let dueDate: Date;
      if (data.dueDate instanceof Timestamp) {
        dueDate = data.dueDate.toDate();
      } else if (data.dueDate instanceof Date) {
        dueDate = data.dueDate;
      } else if (typeof data.dueDate === 'string') {
        dueDate = new Date(data.dueDate);
        // If invalid date, skip this corrective action
        if (isNaN(dueDate.getTime())) {
          return;
        }
      } else {
        return;
      }

      // Apply date filter if specified
      if (filter && !isWithinDateRange(dueDate, filter)) {
        return;
      }

      // Find the assignee
      const assigneeId = data.owner || '';
      const assignee = assignees.find(a => a.userID === assigneeId);
      const assigneeName = assignee 
        ? `${assignee.firstName} ${assignee.lastName}`
        : 'Unassigned';

      // Apply user filter if specified
      if (filter?.userId && assigneeId && assigneeId !== filter.userId) {
        return;
      }

      // Create compliance event
      correctiveActions.push({
        id: docSnap.id,
        date: dueDate.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).split('/').reverse().join('-'),
        title: data.summary || 'Corrective Action',
        type: 'corrective-action',
        description: data.summary || 'Corrective Action',
        assignee: assigneeId, // Store user ID instead of display name
        nextReviewDate: dueDate.toISOString(),
        rawData: data
      });
    });

    console.log(`✅ [ComplianceService] Loaded ${correctiveActions.length} corrective actions`);
    return correctiveActions;
  } catch (error) {
    console.error('[complianceService] Error getting corrective actions:', error);
    return [];
  }
}

/**
 * Get assurance tasks for the company
 * @param companyID Company ID
 * @param assignees List of assignees
 * @param filter Optional filtering parameters
 * @returns Promise containing array of assurance task events
 */
async function getAssuranceTasks(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching assurance tasks for company ID: ${companyID}`);

    // Get the assurance document
    const assuranceDoc = await getDoc(doc(db, 'customers', companyID, 'compliance', 'assurance'));
    if (!assuranceDoc.exists()) {
      console.log('[complianceService] No assurance document found');
      return [];
    }

    const assuranceData = assuranceDoc.data();
    const tasks: ComplianceEvent[] = [];

    // Based on the provided screenshots, we can see the checkFrequency.nextCheck field contains the date
    // We'll filter assurance tasks here based on this field
    for (const [assID, taskData] of Object.entries(assuranceData)) {
      const taskDetail = taskData as AssuranceTaskRaw;

      // Skip inactive tasks
      if (taskDetail?.checkDetail?.active === false || taskDetail?.checkDetail?.active === 'false') {
        continue;
      }

      // Get the next check date
      const nextCheck = taskDetail?.checkFrequency?.nextCheck;
      if (!nextCheck) {
        continue;
      }

      // Convert to Date
      let nextCheckDate: Date;
      if (nextCheck instanceof Timestamp) {
        nextCheckDate = nextCheck.toDate();
      } else if (nextCheck instanceof Date) {
        nextCheckDate = nextCheck;
      } else {
        nextCheckDate = new Date(nextCheck as any);
      }

      // Apply date filter if specified
      if (filter && (filter.startDate || filter.endDate)) {
        // Skip tasks outside date range
        if (!isWithinDateRange(nextCheckDate, filter)) {
          continue;
        }
      }

      // Find the assignee
      const assigneeId = taskDetail.checkDetail?.assignedTo || '';
      const assignee = assignees.find(a => a.userID === assigneeId);
      const assigneeName = assignee
        ? `${assignee.firstName} ${assignee.lastName}`
        : 'Unassigned';

      // Create compliance event
      tasks.push({
        id: taskDetail.assID || assID,
        date: nextCheckDate.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).split('/').reverse().join('-'),
        title: taskDetail.checkDetail.check,
        type: 'assurance-task',
        description: taskDetail.checkDetail.check,
        assignee: assigneeName,
        nextReviewDate: nextCheckDate,
        rawData: taskDetail
      });
    }

    console.log(`✅ [ComplianceService] Loaded ${tasks.length} assurance tasks`);
    return tasks;
  } catch (error) {
    console.error('[complianceService] Error getting assurance tasks:', error);
    return [];
  }
}

/**
 * Get assets for the company
 * @param companyID Company ID
 * @param assignees List of assignees
 * @param filter Optional filtering parameters
 * @returns Promise containing array of asset review events
 */
async function getAssets(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching assets for company ID: ${companyID}`);

    // Use the Asset Register namespace if available
    let assets;
    try {
      assets = await AssetRegister.Asset.Get();
    } catch (error) {
      console.warn('[complianceService] AssetRegister not available, using direct Firestore query');

      // Fallback to direct Firestore query
      const assetsCollection = await getDocs(collection(db, 'customers', companyID, 'assets'));
      assets = {};
      assetsCollection.docs.forEach(doc => {
        assets[doc.id] = { ...doc.data(), assetID: doc.id };
      });
    }

    // Process assets
    const assetsList: ComplianceEvent[] = [];
    for (const [assetId, assetData] of Object.entries(assets)) {
      const asset = assetData as AssetRaw;

      // Skip deleted or decommissioned assets
      if (
        (asset.assetIDstatus === 'deleted') ||
        (asset.assetIDStatus === 'deleted') ||
        (asset.status?.toLowerCase() === 'decommissioned') ||
        (asset.status?.toLowerCase() === 'new inactive')
      ) {
        continue;
      }

      // Utility to robustly parse Firestore Timestamp, ISO string, Date, or epoch
      function parseDateFlexible(value: any): Date | null {
        if (!value) return null;
        // Firestore Timestamp or similar (has toDate method)
        if (typeof value === 'object' && value !== null && typeof value.toDate === 'function') {
          return value.toDate();
        }
        // Firestore Timestamp-like object with _seconds/_nanoseconds
        if (
          typeof value === 'object' && value !== null &&
          typeof value._seconds === 'number' && typeof value._nanoseconds === 'number'
        ) {
          // _seconds is epoch seconds
          return new Date(value._seconds * 1000);
        }
        // Already a Date
        if (value instanceof Date) {
          return value;
        }
        // ISO string or epoch
        if (typeof value === 'string' || typeof value === 'number') {
          const d = new Date(value);
          if (!isNaN(d.getTime())) return d;
        }
        return null;
      }

      // Determine next review date
      let nextReviewDate: Date | null = null;
      if (!asset.nextReviewDate && asset.dateAdded) {
        // If no next review date, add 30 days to date added
        const dateAdded = parseDateFlexible(asset.dateAdded);
        if (dateAdded) {
          nextReviewDate = new Date(dateAdded);
          nextReviewDate.setDate(dateAdded.getDate() + 30);
        }
      } else if (asset.nextReviewDate) {
        nextReviewDate = parseDateFlexible(asset.nextReviewDate);
      }
      if (!nextReviewDate) continue; // Skip if can't parse


      // Apply date filter if specified
      if (filter && (filter.startDate || filter.endDate)) {
        // Skip assets outside the date range
        if (!isWithinDateRange(nextReviewDate, filter)) {
          continue;
        }
      }

      // Find asset owner - apply user filter if specified
      const ownerID = asset.assetOwner;
      let ownerName = 'Unassigned';
      if (ownerID && ownerID !== 'createUser' && ownerID !== '') {
        const assignee = assignees.find(a => a.userID === ownerID);
        if (assignee) {
          ownerName = `${assignee.firstName} ${assignee.lastName}`;
        }
      }

      // Apply user filter if specified
      if (filter?.userId && ownerID !== filter.userId) {
        continue;
      }

      // Create compliance event
      assetsList.push({
        id: asset.assetID,
        date: nextReviewDate.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).split('/').reverse().join('-'),
        title: `${asset.assetDesc}`,
        type: 'asset-review',
        description: asset.assetDesc,
        assignee: ownerName,
        nextReviewDate: nextReviewDate,
        rawData: asset
      });
    }

    console.log(`✅ [ComplianceService] Loaded ${assetsList.length} assets`);
    return assetsList;
  } catch (error) {
    console.error('[complianceService] Error getting assets:', error);
    return [];
  }
}

/**
 * Get risks for the company
 * @param companyID Company ID
 * @param assignees List of assignees
 * @param filter Optional filtering parameters
 * @returns Promise containing array of risk review events
 */
async function getRisks(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching risks for company ID: ${companyID}`);

    // Use the Risk Register namespace if available
    let risks;
    try {
      risks = await RiskRegister.Risk.Get();
    } catch (error) {
      console.warn('[complianceService] RiskRegister not available, using direct Firestore query');

      // Fallback to direct Firestore query
      const risksCollection = await getDocs(collection(db, 'customers', companyID, 'risks'));
      risks = {};
      risksCollection.docs.forEach(doc => {
        risks[doc.id] = { ...doc.data(), riskID: doc.id };
      });
    }

    // Process risks
    const risksList: ComplianceEvent[] = [];
    for (const [riskId, riskData] of Object.entries(risks)) {
      const risk = riskData as RiskRaw;

      // Skip deleted risks
      if (risk.riskIDstatus === 'deleted') {
        continue;
      }

      // Skip risks without a next review date (check both nextReviewDate and nextReview for backward compatibility)
      const reviewDate = risk.nextReviewDate || risk.nextReview;
      if (!reviewDate) {
        continue;
      }

      // Get next review date
      let nextReviewDate: Date;
      if (typeof reviewDate === 'object' && 'toDate' in reviewDate) {
        nextReviewDate = reviewDate.toDate();
      } else {
        nextReviewDate = new Date(reviewDate as any);
      }

      // Apply date filter if specified
      if (filter && (filter.startDate || filter.endDate)) {
        // Skip risks outside the date range
        if (!isWithinDateRange(nextReviewDate, filter)) {
          continue;
        }
      }

      // Find risk owner
      const ownerId = risk.riskOwner;
      let ownerName = 'Unassigned';
      if (ownerId && ownerId !== 'createUser' && ownerId !== '') {
        const assignee = assignees.find(a => a.userID === ownerId);
        if (assignee) {
          ownerName = `${assignee.firstName} ${assignee.lastName}`;
        }
      }

      // Apply user filter if specified
      if (filter?.userId && ownerId !== filter.userId) {
        continue;
      }

      // Create compliance event
      risksList.push({
        id: risk.riskID,
        date: nextReviewDate.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).split('/').reverse().join('-'),
        title: `${risk.risk}`,
        type: 'risk-review',
        description: risk.risk,
        assignee: ownerName,
        nextReviewDate: nextReviewDate,
        rawData: risk
      });
    }

    console.log(`✅ [ComplianceService] Loaded ${risksList.length} risks`);
    return risksList;
  } catch (error) {
    console.error('[complianceService] Error getting risks:', error);
    return [];
  }
}

/**
 * Get supporting documents for the company
 * @param companyID Company ID
 * @param assignees List of assignees
 * @param filter Optional filtering parameters
 * @returns Promise containing array of document review events
 */
async function getSupportingDocs(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching supporting docs for company ID: ${companyID}`);

    // Get the supporting documents
    const supportingDocsRef = doc(
      db,
      'customers',
      companyID,
      'product',
      'policyPackages',
      'ISO27001keyDocuments',
      'otherDocs'
    );

    const supportingDocsDoc = await getDoc(supportingDocsRef);
    if (!supportingDocsDoc.exists()) {
      console.log('[complianceService] No supporting documents found');
      return [];
    }

    const supportingDocsData = supportingDocsDoc.data();
    if (!supportingDocsData.uploaded || Object.keys(supportingDocsData.uploaded).length === 0) {
      return [];
    }

    // Process supporting documents
    const docsList: ComplianceEvent[] = [];
    for (const [docId, docData] of Object.entries(supportingDocsData.uploaded)) {
      const doc = docData as SupportingDocRaw;

      // Determine next review date
      let nextReviewDate: Date;
      if (!doc.nextReviewDate && doc.dateUploaded) {
        console.log('No next review date found, adding 12 months to upload date');
        // If no next review date, add 12 months to upload date
        let uploadDate: Date;
        if (typeof doc.dateUploaded === 'object' && 'toDate' in doc.dateUploaded) {
          console.log('Date uploaded is a Firebase Timestamp');
          uploadDate = doc.dateUploaded.toDate();
        } else {
          console.log('Date uploaded is a Date object');
          uploadDate = new Date(doc.dateUploaded as any);
        }

        nextReviewDate = new Date(uploadDate);
        nextReviewDate.setMonth(uploadDate.getMonth() + 12);
      } else {
        if (typeof doc.nextReviewDate === 'object' && 'toDate' in doc.nextReviewDate) {
          console.log('Next review date is a Firebase Timestamp');
          nextReviewDate = doc.nextReviewDate.toDate();
        } else {
          console.log('Next review date is a Date object');
          nextReviewDate = new Date(doc.nextReviewDate as any);
        }
      }

      let lastReviewDate: Date;
      if (!doc.lastReviewDate && doc.dateUploaded) {
        console.log('No last review date found, adding 12 months to upload date');
        // If no last review date, add 12 months to upload date
        let uploadDate: Date;
        if (typeof doc.dateUploaded === 'object' && 'toDate' in doc.dateUploaded) {
          console.log('Date uploaded is a Firebase Timestamp');
          uploadDate = doc.dateUploaded.toDate();
        } else {
          console.log('Date uploaded is a Date object');
          uploadDate = new Date(doc.dateUploaded as any);
        }

        lastReviewDate = new Date(uploadDate);
        lastReviewDate.setMonth(uploadDate.getMonth() + 12);
      } else {
        if (typeof doc.lastReviewDate === 'object' && 'toDate' in doc.lastReviewDate) {
          console.log('Last review date is a Firebase Timestamp');
          lastReviewDate = doc.lastReviewDate.toDate();
        } else {
          console.log('Last review date is a Date object');
          lastReviewDate = new Date(doc.lastReviewDate as any);
        }
      }

      // Apply date filter if specified
      if (filter && (filter.startDate || filter.endDate)) {
        // Skip documents outside the date range
        if (!isWithinDateRange(nextReviewDate, filter)) {
          continue;
        }
      }

      // Find document owner/uploader
      const uploaderID = doc.uploadedBy;
      let uploaderName = 'Unassigned';
      if (uploaderID) {
        const assignee = assignees.find(a => a.userID === uploaderID);
        if (assignee) {
          uploaderName = `${assignee.firstName} ${assignee.lastName}`;
        }
      }

      // Apply user filter if specified
      if (filter?.userId && uploaderID !== filter.userId) {
        continue;
      }

      // Create compliance event
      docsList.push({
        id: doc.docID || docId,
        date: nextReviewDate.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).split('/').reverse().join('-'),
        title: `${doc.fileName}`,
        type: 'document-review',
        description: doc.fileName,
        assignee: uploaderName,
        lastReviewDate: lastReviewDate,
        nextReviewDate: nextReviewDate,
        rawData: doc
      });

      console.log('✅ [ComplianceService] Loaded document:', doc, docsList)
    }

    console.log(`✅ [ComplianceService] Loaded ${docsList.length} supporting documents`);
    return docsList;
  } catch (error) {
    console.error('[complianceService] Error getting supporting docs:', error);
    return [];
  }
}

/**
 * Get compliance events assigned to the current user with optional filtering
 * @param companyID The company ID to fetch events for
 * @param filter Optional filtering parameters
 * @returns Promise containing array of compliance events
 */
export async function getUserComplianceEvents(companyID: string, userObj: string, filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  console.log(`🔄 [ComplianceService] Fetching user-specific compliance events for company: ${companyID}`);
  if (filter) {
    console.log('📅 [ComplianceService] Applying filters for user events:', {
      startDate: filter.startDate ? new Date(filter.startDate).toISOString().split('T')[0] : 'none',
      endDate: filter.endDate ? new Date(filter.endDate).toISOString().split('T')[0] : 'none',
      view: filter.view || 'none'
    });
  }

  try {
    // Get all compliance data
    const allData = await getComplianceData(companyID, filter);

    // Get current user from auth state
    const currentUser = getAuthUser();
    if (!currentUser) {
      console.warn('⚠️ [ComplianceService] No current user found, returning all events');
      return allData.allEvents;
    }

    console.log(`👤 [ComplianceService] Filtering events for user: ${((userObj as any).firstName + ' ' + (userObj as any).lastName) || currentUser.email} (${currentUser.uid})`);

    // Filter events assigned to current user
    const userEvents = allData.allEvents.filter(event => {
      // If the event has an assignee field, check if it matches the current user
      // Note: This is a simplified version - in a real app, you'd have a more sophisticated matching logic
      return event.assignee === ((userObj as any).firstName + ' ' + (userObj as any).lastName) ||
        event.assignee === currentUser.email ||
        event.assignee === currentUser.uid;
    });

    console.log(`✅ [ComplianceService] Found ${userEvents.length} events assigned to current user`);
    console.log(`📊 [ComplianceService] User events are ${Math.round((userEvents.length / allData.allEvents.length) * 100)}% of all events`);
    return userEvents;
  } catch (error) {
    console.error('[ComplianceService] Error fetching user compliance events:', error);
    return [];
  }
}


async function getPolicies(companyID: string, assignees: Assignee[], filter?: ComplianceDataFilter): Promise<ComplianceEvent[]> {
  try {
    console.log(`🔍 [ComplianceService] Fetching policies for company ID: ${companyID}`);
    const policies = await getPolicyList(companyID);

    const policyEvents: ComplianceEvent[] = [];

    for (const policy of policies) {
      const policyAny = policy as any;
      
      // Skip policies that don't have addToCalendar enabled (unless they have a nextReviewDate for legacy support)
      const hasCalendarEnabled = policyAny.addToCalendar;
      const nextReviewDate = parseChecklistDate(policyAny.nextReviewDate || policyAny.policy_history?.nextReviewDate);
      const repeat = policyAny.repeat;
      
      // Skip if no calendar date set
      if (!hasCalendarEnabled && !nextReviewDate) continue;

      let assigneeName = 'Unassigned';
      const assignedUserId = policyAny.policy_metadata?.assignedTo || policyAny.uploadedBy || policyAny.policy_history?.policy_owner;

      if (assignedUserId) {
        const assignee = assignees.find(a => a.userID === assignedUserId);
        if (assignee) {
          assigneeName = `${assignee.firstName} ${assignee.lastName}`;
        }
      }

      // Apply user filter if specified
      if (filter?.userId && assignedUserId !== filter.userId) {
        continue;
      }

      // Handle recurring policies
      if (repeat && repeat.frequency && repeat.frequency !== 'never') {
        const start = parseChecklistDate(repeat.startDate ?? nextReviewDate ?? null);
        const end = repeat.endDate ? parseChecklistDate(repeat.endDate) : null;
        
        if (!start) continue;
        
        // Convert to ChecklistRepeatConfig format
        const checklistRepeat: ChecklistRepeatConfig = {
          frequency: repeat.frequency as any,
          interval: repeat.interval || 1,
          startDate: start,
          endDate: end ?? undefined,
        };
        
        const occurrences = generateRecurringOccurrences(start, checklistRepeat, filter);
        
        occurrences.forEach(({ occurrence, previous }) => {
          if (!isWithinDateRange(occurrence, filter)) return;
          
          policyEvents.push({
            id: `policy-${policyAny.id}-${formatDateToYMD(occurrence)}`,
            date: formatDateToYMD(occurrence),
            title: `${policyAny.policy_name || policyAny.name || 'Untitled'}`,
            description: policyAny.policy_metadata?.file_name || '',
            type: 'policy-review',
            assignee: assigneeName,
            prevReviewDate: previous ? formatDateToYMD(previous) : undefined,
            nextReviewDate: formatDateToYMD(occurrence),
            startDate: formatDateToYMD(start),
            endDate: end ? formatDateToYMD(end) : undefined,
            interval: repeat.interval,
            frequency: repeat.frequency,
            rawData: policy
          });
        });
      } else if (nextReviewDate) {
        // One-time review date
        if (!isWithinDateRange(nextReviewDate, filter)) continue;
        
        policyEvents.push({
          id: policyAny.id,
          date: formatDateToYMD(nextReviewDate),
          title: `${policyAny.policy_name || policyAny.name || 'Untitled'}`,
          description: policyAny.policy_metadata?.file_name || '',
          type: 'policy-review',
          assignee: assigneeName,
          nextReviewDate: formatDateToYMD(nextReviewDate),
          rawData: policy
        });
      }
    }

    console.log(`✅ [ComplianceService] Loaded ${policyEvents.length} policy documents`);
    return policyEvents;
  } catch (error) {
    console.error('[complianceService] Error getting policy documents:', error);
    return [];
  }
}

// Mock data functions have been removed as they are no longer needed
// Real Firebase implementation is now used
